import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Resident, Image } from '@zs/shared/schema';
import { fetchResident } from '../api';

const IMAGE_BASE_URL = import.meta.env.VITE_IMAGE_URL || '';

const STATUS_LABELS: Record<string, string> = {
  'new': 'Новий',
  'in-clinic': 'На лікуванні',
  'kept': 'Готовий до усиновлення',
  'adopted': 'Усиновлений',
  'dead': 'Помер',
};

function getImageUrl(image: Image): string {
  return `${IMAGE_BASE_URL}/images/${image.storageKey}`;
}

export function ResidentStory() {
  const { id } = useParams<{ id: string }>();
  const [resident, setResident] = useState<Resident | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    
    fetchResident(id)
      .then((data) => {
        setResident(data);
      })
      .catch(() => {
        setResident(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="default-loading">
        <div className="default-spinner"></div>
        <p>Завантаження...</p>
      </div>
    );
  }

  if (!resident) {
    return (
      <div className="default-story-page">
        <Link to="/" className="default-story-back">
          ← Назад до всіх резидентів
        </Link>
        <div className="default-loading">
          <p>Резидента не знайдено</p>
        </div>
      </div>
    );
  }

  const images = resident.images || [];
  const maxSlide = Math.max(0, images.length - 1);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    (e.target as HTMLElement).dataset.startX = String(touch.clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endX = e.changedTouches[0].clientX;
    const startX = parseInt((e.target as HTMLElement).dataset.startX || '0');
    const diff = startX - endX;
    
    if (diff > 50 && currentSlide < maxSlide) {
      setCurrentSlide(currentSlide + 1);
    } else if (diff < -50 && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="default-story-page">
      <Link to="/" className="default-story-back">
        ← Назад
      </Link>

      <section className="default-story-carousel">
        <div
          className="default-story-track"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {images.length > 0 ? (
            images.map((image, i) => (
              <div key={image._id} className="default-story-slide">
                <img src={getImageUrl(image)} alt={`${resident.name} - фото ${i + 1}`} />
              </div>
            ))
          ) : (
            <div className="default-story-slide">
              <div className="default-story-placeholder">
                <span>📷</span>
                <small>Немає фото</small>
              </div>
            </div>
          )}
        </div>
        {images.length > 1 && (
          <div className="default-carousel-dots">
            {images.map((_, i) => (
              <button
                key={i}
                className={`default-carousel-dot ${i === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(i)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="default-story-meta">
        <h1 className="default-story-name">{resident.name}</h1>
        <p className="default-story-species">
          {resident.species || 'Тварина'} {resident.breed ? `· ${resident.breed}` : ''}
        </p>
        <div className="default-story-info-grid">
          <div className="default-story-info-item">
            <div className="default-story-info-label">Вік</div>
            <div className="default-story-info-value">~2 роки</div>
          </div>
          <div className="default-story-info-item">
            <div className="default-story-info-label">Статус</div>
            <div className="default-story-info-value">
              {STATUS_LABELS[resident.status] || resident.status}
            </div>
          </div>
          <div className="default-story-info-item">
            <div className="default-story-info-label">ID</div>
            <div className="default-story-info-value">#{resident._id.padStart(3, '0')}</div>
          </div>
        </div>
      </section>

      <section className="default-story-cta">
        <p className="default-story-cta-text">
          {resident.name} мріє про теплий дім та люблячу родину. Допоможіть знайти йому щастя!
        </p>
        <a
          href="https://instagram.com"
          className="default-story-cta-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
          </svg>
          Запитати про {resident.name}
        </a>
      </section>
    </div>
  );
}
